import React, { useMemo } from "react";
import dayjs from "dayjs";
import { EGBasicSelect } from "../../../MainComponents/Select/EGBasicSelect";
import { useReportContext } from "../../../Report/ReportContext/useReportContext";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGButton } from "../../../MainComponents/Button/EGButton/EGButton";
import { Settings4 } from "../../../Icon";
import { EGIconButton } from "../../../MainComponents/Button/IconButton/IconButton";
import { EGCheckbox } from "../../../MainComponents/Checkbox/EGCheckbox";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export const AdvancedSearch = ({
  selectFilters,
  setSelectFilters,
  openAdvanceSearch,
  setOpenAdvanceSearch,
  searchingInfo,
}) => {
  // Custom hooks
  const theme = useTheme();
  const [t] = useTranslation("report");
  const { statusOptions, kindOfEntryways, severityOptions } =
    useReportContext();

  // Master Switch
  const handleToggleAll = () => {
    const allActive = Object.values(selectFilters).every((f) => f.active);

    setSelectFilters((prevFilters) =>
      Object.keys(prevFilters).reduce((newFilters, key) => {
        newFilters[key] = { ...prevFilters[key], active: !allActive };
        return newFilters;
      }, {})
    );
  };

  // Options year created
  const optionsYearCreated = useMemo(() => {
    const currentYear = dayjs().year();
    return Array.from({ length: currentYear - 2015 + 1 }, (_, index) => {
      const year = currentYear - index;
      return { value: year.toString(), label: year.toString() };
    });
  }, []);

  const handleSelectChange = (filterKey, newValue) => {
    setSelectFilters((prevFilters) => ({
      ...prevFilters,
      [filterKey]: { ...prevFilters[filterKey], value: newValue },
    }));
  };

  const handleCheckboxChange = (filterKey) => {
    setSelectFilters((prevFilters) => ({
      ...prevFilters,
      [filterKey]: {
        ...prevFilters[filterKey],
        active: !prevFilters[filterKey]?.active,
      },
    }));
  };

  const getResults = async () => {
    await searchingInfo();
    setOpenAdvanceSearch(false);
  };

  return (
    <>
      <Dialog
        open={openAdvanceSearch}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="alert-dialog-slide-advance-search"
        maxWidth={"md"}
        fullWidth
      >
        <EGIconButton
          iconName="cancel_circle"
          iconColor="primary"
          sx={{ position: "absolute", top: 10, right: 10 }}
          onClick={() => setOpenAdvanceSearch(false)}
        />

        <DialogTitle className="border-bottom">
          <Typography
            variant="h5"
            component="span"
            fontWeight={600}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <Settings4
              style={{ marginRight: "15px", transform: "rotate(90deg)" }}
              color={theme.palette.secondary.main}
            />
            {t("searchBar.advanced_search")}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack sx={{ width: "100%", mt: 1 }}>
            <EGCheckbox
              checked={Object.values(selectFilters).every((f) => f.active)}
              onChange={handleToggleAll}
              label={
                Object.values(selectFilters).every((f) => f.active)
                  ? t("Filter.deselect_all")
                  : t("Filter.select_all")
              }
              labelPlacement="end"
              color="primary"
            />
          </Stack>
          <Stack
            spacing={1}
            alignItems="stretch"
            justifyContent="space-between"
            flexDirection="row"
            flexWrap="wrap"
            my={1}
            px={1}
          >
            <Stack
              sx={{
                width: { xs: "100%", sm: "48%" },
              }}
            >
              <EGCheckbox
                checked={selectFilters?.status?.active}
                onChange={() => handleCheckboxChange("status")}
                label={t("Filter.status")}
                labelPlacement="end"
                color="primary"
              />
              {selectFilters?.status?.active && (
                <EGBasicSelect
                  options={statusOptions}
                  label={`${t("Filter.status")}:`}
                  value={selectFilters.status.value}
                  onChange={(e) => handleSelectChange("status", e.target.value)}
                />
              )}
            </Stack>
            <Stack
              sx={{
                width: { xs: "100%", sm: "48%" },
              }}
            >
              <EGCheckbox
                checked={selectFilters?.entryway?.active}
                onChange={() => handleCheckboxChange("entryway")}
                label={t("Filter.kind_of_entryways")}
                labelPlacement="end"
                color="primary"
              />
              {selectFilters?.entryway?.active && (
                <EGBasicSelect
                  options={kindOfEntryways}
                  value={selectFilters.entryway.value}
                  onChange={(e) =>
                    handleSelectChange("entryway", e.target.value)
                  }
                  label={`${t("Filter.kind_of_entryways")}:`}
                />
              )}
            </Stack>
            <Stack
              sx={{
                width: { xs: "100%", sm: "48%" },
              }}
            >
              <EGCheckbox
                checked={selectFilters?.year?.active}
                onChange={() => handleCheckboxChange("year")}
                label={t("Filter.year")}
                labelPlacement="end"
                color="primary"
              />
              {selectFilters?.year?.active && (
                <EGBasicSelect
                  options={optionsYearCreated}
                  value={selectFilters.year.value}
                  onChange={(e) => handleSelectChange("year", e.target.value)}
                  label={t("Filter.year")}
                />
              )}
            </Stack>
            <Stack
              sx={{
                width: { xs: "100%", sm: "48%" },
              }}
            >
              <EGCheckbox
                checked={selectFilters?.severity?.active}
                onChange={() => handleCheckboxChange("severity")}
                label={t("Filter.kind_of_severity")}
                labelPlacement="end"
                color="primary"
              />
              {selectFilters?.severity?.active && (
                <EGBasicSelect
                  options={severityOptions}
                  value={selectFilters.severity.value}
                  onChange={(e) =>
                    handleSelectChange("severity", e.target.value)
                  }
                  label={`${t("Filter.kind_of_severity")}:`}
                />
              )}
            </Stack>
          </Stack>

          <DialogActions>
            <EGButton
              label={t("Filter.cancel")}
              color="secondary"
              onClick={() => setOpenAdvanceSearch(false)}
            />
            <EGButton
              label={t("Filter.apply_filter")}
              onClick={getResults}
              color="primary"
            />
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};
